import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { useSiteSettings } from './SiteSettingsContext';
import { AccountAdminUsers, AccountChangeUsername } from './account/AccountSettingsPanels';
import AccountAddressBook from './account/AccountAddressBook';
import AdminAccountHub from './account/AdminAccountHub';
import './admin/admin-controls.css';
import CustomerAccountHub from './account/CustomerAccountHub';
import { AccountCustomerOrders, AccountCustomerProfile } from './account/AccountCustomerPanels';
import { useCart } from './cart/CartContext';
import { useProducts } from './useProducts';
import { useWishlist } from './wishlist/WishlistContext';
import AccountTileIcon, { AccountTileChevron } from './ui/AccountTileIcon';
import { brandNameFromSettings, storeSeo } from './siteBrand';
import PasswordInput from './components/PasswordInput';

function AccountChangePassword({ changePassword }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setBusy(true);
    try {
      const res = await changePassword(currentPassword, newPassword, confirmPassword);
      if (!res.ok) {
        setError(res.error || 'Could not update password.');
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSuccess('Password updated. Use your new password next time you sign in.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account-password" aria-labelledby="account-password-heading">
      <h2 id="account-password-heading" className="account-section-label">
        Password
      </h2>
      <div className="account-password-card">
        <p className="account-password-card__lead small text-muted mb-3">
          Choose a strong password (at least 8 characters). You will stay signed in on this device after updating.
        </p>
        <form onSubmit={onSubmit} noValidate className="account-password-form">
          {error ? (
            <div className="alert alert-danger py-2 small" role="alert">
              {error}
            </div>
          ) : null}
          {success ? (
            <div className="alert alert-success py-2 small" role="status">
              {success}
            </div>
          ) : null}
          <div className="checkout-field mb-3">
            <label className="checkout-field__label" htmlFor="account-current-password">
              Current password
            </label>
            <PasswordInput
              id="account-current-password"
              className="form-control checkout-input"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
              required
            />
          </div>
          <div className="checkout-field mb-3">
            <label className="checkout-field__label" htmlFor="account-new-password">
              New password
            </label>
            <PasswordInput
              id="account-new-password"
              className="form-control checkout-input"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={busy}
              minLength={8}
              required
            />
          </div>
          <div className="checkout-field mb-3">
            <label className="checkout-field__label" htmlFor="account-confirm-password">
              Confirm new password
            </label>
            <PasswordInput
              id="account-confirm-password"
              className="form-control checkout-input"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
              minLength={8}
              required
            />
          </div>
          <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </div>
    </section>
  );
}

function AccountLoading() {
  return (
    <div className="account-page">
      <div className="container account-page__container">
        <div className="account-hero-panel account-hero-panel--loading text-center" aria-busy="true">
          <div className="d-flex align-items-center justify-content-center gap-3 text-muted py-4">
            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <p className="small mb-0">Loading your account…</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AccountPage() {
  const {
    ready,
    isAuthed,
    username,
    isAdmin,
    isStaff,
    isCustomer,
    displayName,
    email,
    phone,
    logout,
    changePassword,
    changeUsername,
    updateProfile,
    listUsers,
    listCustomers,
    createUser,
    updateUserRole,
    resetCustomerPassword,
    listOrders,
    getOrderDetail,
  } = useAuth();
  const { addItem } = useCart();
  const { products } = useProducts();
  const { settings: site } = useSiteSettings();
  const { count: wishlistCount } = useWishlist();
  const [orderCount, setOrderCount] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const showStoreOrders = isCustomer;

  useEffect(() => {
    if (!showStoreOrders || !listOrders) return;
    let cancelled = false;
    listOrders()
      .then((res) => {
        if (cancelled) return;
        if (res.ok && Array.isArray(res.orders)) {
          setOrderCount(res.orders.length);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showStoreOrders, listOrders]);

  if (!ready) {
    return <AccountLoading />;
  }

  if (!isAuthed) {
    return <Navigate to="/login?next=/account" replace />;
  }

  const shownName = (displayName || username)?.trim() || 'Guest';
  const colClass = isAdmin ? 'col-md-6' : 'col-md-4';
  const useCustomerHub = isCustomer && showStoreOrders;
  const useStaffHub = isStaff && !isCustomer;
  const brandName = brandNameFromSettings(site);
  const accountSeo = storeSeo(site, 'seo_account_title', 'My account', 'View orders, saved items, and account details.');
  const accountHelmet = (
    <Helmet>
      <title>{accountSeo.title}</title>
      <meta name="description" content={accountSeo.description} />
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
  );

  if (useCustomerHub) {
    return (
      <div className="account-page account-page--customer">
        {accountHelmet}
        <div className="container account-page__container store-page__container">
          <CustomerAccountHub
            displayName={displayName}
            email={email}
            phone={phone}
            wishlistCount={wishlistCount}
            orderCount={orderCount}
            site={site}
            logout={logout}
            changePassword={changePassword}
            updateProfile={updateProfile}
            listOrders={listOrders}
            getOrderDetail={getOrderDetail}
            addItem={addItem}
            products={products}
          />
        </div>
      </div>
    );
  }

  if (useStaffHub) {
    return (
      <div className="account-page account-page--staff">
        {accountHelmet}
        <div className="container account-page__container store-page__container">
          <AdminAccountHub
            isAdmin={isAdmin}
            displayName={displayName}
            username={username}
            email={email}
            phone={phone}
            site={site}
            logout={logout}
            changePassword={changePassword}
            changeUsername={changeUsername}
            updateProfile={updateProfile}
            listUsers={listUsers}
            listCustomers={listCustomers}
            createUser={createUser}
            updateUserRole={updateUserRole}
            resetCustomerPassword={resetCustomerPassword}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      {accountHelmet}

      <div className="container account-page__container">
        <section className="account-hero shop-hero" aria-labelledby="account-heading">
          <div className="account-hero-panel text-center">
            <p className="shop-hero-eyebrow">{brandName}</p>
            <h1 id="account-heading" className="shop-page-title">
              My account
            </h1>
            <p className="account-signed-in shop-page-subtitle">
              Signed in as <span className="account-username">{shownName}</span>
            </p>
            {isAdmin ? (
              <span className="account-role-badge">Store administrator</span>
            ) : isCustomer ? (
              <span className="account-role-badge">Customer account</span>
            ) : (
              <p className="account-hero-note small text-muted mb-0">
                Manage your session and explore the collections below.
              </p>
            )}
            {showStoreOrders || wishlistCount > 0 ? (
              <div className="account-hero-stats" role="list">
                {showStoreOrders && orderCount !== null ? (
                  <span className="account-hero-stat" role="listitem">
                    {orderCount === 1 ? '1 order' : `${orderCount} orders`}
                  </span>
                ) : null}
                {wishlistCount > 0 ? (
                  <span className="account-hero-stat" role="listitem">
                    {wishlistCount} saved {wishlistCount === 1 ? 'item' : 'items'}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        <section className="account-actions" aria-label="Quick links">
          <h2 className="account-section-label">Quick links</h2>
          <div className="row g-3 g-md-4">
            {isAdmin ? (
              <div className="col-md-6">
                <Link to="/admin" className="account-tile account-tile--primary">
                  <span className="account-tile__icon" aria-hidden="true">
                    <AccountTileIcon name="admin" />
                  </span>
                  <span className="account-tile__body">
                    <span className="account-tile__title">Catalog admin</span>
                    <span className="account-tile__desc">Manage products, categories, and site content</span>
                  </span>
                  <span className="account-tile__arrow" aria-hidden="true">
                    <AccountTileChevron />
                  </span>
                </Link>
              </div>
            ) : null}
            {showStoreOrders ? (
              <>
                <div className={colClass}>
                  <Link to="/cart" className="account-tile account-tile--primary">
                    <span className="account-tile__icon" aria-hidden="true">
                      <AccountTileIcon name="cart" />
                    </span>
                    <span className="account-tile__body">
                      <span className="account-tile__title">Your cart</span>
                      <span className="account-tile__desc">Review items and check out</span>
                    </span>
                    <span className="account-tile__arrow" aria-hidden="true">
                      <AccountTileChevron />
                    </span>
                  </Link>
                </div>
                <div className={colClass}>
                  <Link to="/wishlist" className="account-tile">
                    <span className="account-tile__icon" aria-hidden="true">
                      <AccountTileIcon name="heart" />
                    </span>
                    <span className="account-tile__body">
                      <span className="account-tile__title">Saved items</span>
                      <span className="account-tile__desc">
                        {wishlistCount > 0 ? `${wishlistCount} saved for later` : 'Save pieces from the shop'}
                      </span>
                    </span>
                    <span className="account-tile__arrow" aria-hidden="true">
                      <AccountTileChevron />
                    </span>
                  </Link>
                </div>
                <div className={colClass}>
                  <Link to="/track-order" className="account-tile">
                    <span className="account-tile__icon" aria-hidden="true">
                      <AccountTileIcon name="package" />
                    </span>
                    <span className="account-tile__body">
                      <span className="account-tile__title">Track your order</span>
                      <span className="account-tile__desc">Lookup by order number</span>
                    </span>
                    <span className="account-tile__arrow" aria-hidden="true">
                      <AccountTileChevron />
                    </span>
                  </Link>
                </div>
              </>
            ) : null}
            <div className={colClass}>
              <Link to="/shop" className="account-tile">
                <span className="account-tile__icon" aria-hidden="true">
                  <AccountTileIcon name="shop" />
                </span>
                <span className="account-tile__body">
                  <span className="account-tile__title">Browse shop</span>
                  <span className="account-tile__desc">Rakhi, Lumba, and seasonal collections</span>
                </span>
                <span className="account-tile__arrow" aria-hidden="true">
                  <AccountTileChevron />
                </span>
              </Link>
            </div>
            <div className={colClass}>
              <Link to="/" className="account-tile">
                <span className="account-tile__icon" aria-hidden="true">
                  <AccountTileIcon name="home" />
                </span>
                <span className="account-tile__body">
                  <span className="account-tile__title">Home</span>
                  <span className="account-tile__desc">Featured products and upcoming exhibitions</span>
                </span>
                <span className="account-tile__arrow" aria-hidden="true">
                  <AccountTileChevron />
                </span>
              </Link>
            </div>
            <div className={colClass}>
              <a
                href={site.whatsapp_href || 'https://wa.me/919680384332'}
                className="account-tile"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="account-tile__icon" aria-hidden="true">
                  <AccountTileIcon name="message" />
                </span>
                <span className="account-tile__body">
                  <span className="account-tile__title">WhatsApp</span>
                  <span className="account-tile__desc">Questions, custom orders, or festival timing</span>
                </span>
                <span className="account-tile__arrow" aria-hidden="true">
                  <AccountTileChevron />
                </span>
              </a>
            </div>
          </div>
        </section>

        {showStoreOrders ? (
          <div className="account-dashboard">
            <div className="account-dashboard__primary">
              <AccountCustomerOrders
                listOrders={listOrders}
                getOrderDetail={getOrderDetail}
                addItem={addItem}
                products={products}
              />
            </div>
            <div className="account-dashboard__secondary">
              <AccountAddressBook />
              <AccountCustomerProfile
                displayName={displayName}
                email={email}
                phone={phone}
                updateProfile={updateProfile}
              />
            </div>
          </div>
        ) : (
          <AccountChangeUsername changeUsername={changeUsername} currentUsername={shownName} />
        )}

        <AccountChangePassword changePassword={changePassword} />

        {isAdmin ? (
          <AccountAdminUsers
            listUsers={listUsers}
            createUser={createUser}
            updateUserRole={updateUserRole}
            currentUsername={shownName}
          />
        ) : null}

        <section className="account-session" aria-label="Session">
          <div className="account-session-card">
            <div className="account-session-card__text">
              <h2 className="account-session-card__title handwritten">Session</h2>
              <p className="account-session-card__desc small mb-0">
                You can also sign out from the <strong>Account</strong> menu in the header.
              </p>
            </div>
            <div className="account-session-card__actions d-flex flex-wrap gap-2 justify-content-center justify-content-md-end">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={() => void logout()}
              >
                Sign out
              </button>
              <Link to="/contact" className="btn btn-link rounded-pill text-decoration-none">
                Contact us
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
